import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import LogoutIcon from '@mui/icons-material/Logout';
import { isAdmin, signOutRedirect } from '../utils/authUtils.ts';
import { useAuthenticatedAxios } from '../services/useAuthenticatedAxios';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { playlistService, mediaService } from '../services/api.ts';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Paper from '@mui/material/Paper';

const Input = styled('input')({
  display: 'none',
});

const AdminPage = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const navigate = useNavigate();
  const auth = useAuth();
  const api = useAuthenticatedAxios();
  const [playlists, setPlaylists] = useState<{playlistId: string, playlistName: string}[]>([]);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newPlaylistDescription, setNewPlaylistDescription] = useState('');
  const [newPlaylistPicUrl, setNewPlaylistPicUrl] = useState('');
  const [playlistCreateLoading, setPlaylistCreateLoading] = useState(false);
  const [playlistCreateSuccess, setPlaylistCreateSuccess] = useState<string|null>(null);
  const [playlistCreateError, setPlaylistCreateError] = useState<string|null>(null);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState('');
  const [mediaName, setMediaName] = useState('');
  const [mediaDescription, setMediaDescription] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [createPlaylistDialogOpen, setCreatePlaylistDialogOpen] = useState(false);

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.access_token) {
      navigate('/');
      return;
    }

    if (!isAdmin(auth.user.access_token)) {
      navigate('/');
    }
    fetchPlaylists();
  }, [auth.isAuthenticated, auth.user?.access_token, navigate]);

  const fetchPlaylists = async () => {
    try {
      const response = await playlistService.listPlaylists(api);
      if (response.success) {
        setPlaylists(response.data.map((p: any) => ({ playlistId: p.playlistId, playlistName: p.playlistName })));
      }
    } catch (e) {
      // ignore for now
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
      setSuccess(null);
      setUploadProgress(0);
    }
  };

  const handleCreatePlaylist = async (e: React.FormEvent) => {
    e.preventDefault();
    setPlaylistCreateLoading(true);
    setPlaylistCreateSuccess(null);
    setPlaylistCreateError(null);
    try {
      await playlistService.createPlaylist(api, newPlaylistName, newPlaylistDescription, newPlaylistPicUrl);
      setPlaylistCreateSuccess('Playlist created successfully!');
      setNewPlaylistName('');
      setNewPlaylistDescription('');
      setNewPlaylistPicUrl('');
      fetchPlaylists();
    } catch (err) {
      setPlaylistCreateError('Failed to create playlist.');
    } finally {
      setPlaylistCreateLoading(false);
    }
  };

  const handleUploadToPlaylist = async () => {
    if (!selectedFile || !selectedPlaylistId || !mediaName) {
      setError('Please select a file, playlist, and enter a media name');
      return;
    }
    setUploading(true);
    setError(null);
    setSuccess(null);
    setUploadProgress(0);
    try {
      await mediaService.createMedia(api, selectedFile, mediaName, mediaDescription, selectedPlaylistId);
      setSuccess('Video uploaded to playlist successfully!');
      setSelectedFile(null);
      setMediaName('');
      setMediaDescription('');
      setSelectedPlaylistId('');
      setUploadProgress(0);
    } catch (err) {
      setError('Failed to upload video to playlist.');
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = () => {
    signOutRedirect(auth);
  };

  if (!auth.isAuthenticated || !auth.user?.access_token) {
    return null;
  }

  return (
    <Box sx={{ flexGrow: 1, background: '#f5f7fa', minHeight: '100vh' }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Admin Dashboard - Video Upload
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 6, mb: 6 }}>
        {/* Create Playlist Button */}
        <Paper elevation={3} sx={{ p: 4, mb: 6, borderRadius: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setCreatePlaylistDialogOpen(true)}
            sx={{ mt: 2, mb: 2, px: 4, py: 1.5, fontWeight: 600, fontSize: '1.1rem', borderRadius: 2, boxShadow: 2 }}
          >
            Create New Playlist
          </Button>
        </Paper>

        {/* Create Playlist Dialog */}
        <Dialog open={createPlaylistDialogOpen} onClose={() => setCreatePlaylistDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Playlist</DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleCreatePlaylist} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, maxWidth: 500 }}>
              <TextField
                label="Playlist Name"
                value={newPlaylistName}
                onChange={e => setNewPlaylistName(e.target.value)}
                required
              />
              <TextField
                label="Playlist Description"
                value={newPlaylistDescription}
                onChange={e => setNewPlaylistDescription(e.target.value)}
                required
              />
              <TextField
                label="Playlist Picture URL"
                value={newPlaylistPicUrl}
                onChange={e => setNewPlaylistPicUrl(e.target.value)}
                placeholder="Optional"
              />
              {playlistCreateSuccess && <Alert severity="success">{playlistCreateSuccess}</Alert>}
              {playlistCreateError && <Alert severity="error">{playlistCreateError}</Alert>}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreatePlaylistDialogOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              onClick={handleCreatePlaylist}
              disabled={playlistCreateLoading}
            >
              {playlistCreateLoading ? <CircularProgress size={24} /> : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Upload Video to Playlist Button */}
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
            Upload Video to Playlist
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setUploadDialogOpen(true)}
            sx={{ mt: 2, mb: 2, px: 4, py: 1.5, fontWeight: 600, fontSize: '1.1rem', borderRadius: 2, boxShadow: 2 }}
          >
            Upload Video to Playlist
          </Button>
        </Paper>

        {/* Upload Video to Playlist Dialog */}
        <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Upload Video to Playlist</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                select
                label="Select Playlist"
                value={selectedPlaylistId}
                onChange={e => setSelectedPlaylistId(e.target.value)}
                required
              >
                {playlists.map((p) => (
                  <MenuItem key={p.playlistId} value={p.playlistId}>{p.playlistName}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Media Name"
                value={mediaName}
                onChange={e => setMediaName(e.target.value)}
                required
              />
              <TextField
                label="Media Description"
                value={mediaDescription}
                onChange={e => setMediaDescription(e.target.value)}
              />
              <label htmlFor="video-upload-playlist">
                <Input
                  accept="video/*"
                  id="video-upload-playlist"
                  type="file"
                  onChange={handleFileSelect}
                  disabled={uploading}
                />
                <Button
                  variant="contained"
                  component="span"
                  disabled={uploading}
                  sx={{ mr: 2 }}
                >
                  Select Video
                </Button>
                {selectedFile && (
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    Selected file: {selectedFile.name}
                  </Typography>
                )}
              </label>
              {uploading && (
                <Box sx={{ width: '100%', mt: 2 }}>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Uploading: {uploadProgress}%
                  </Typography>
                </Box>
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {success}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUploadDialogOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUploadToPlaylist}
              disabled={!selectedFile || uploading || !selectedPlaylistId || !mediaName}
            >
              {uploading ? <CircularProgress size={24} /> : 'Upload'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdminPage; 